import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { ArrowLeft, ArrowRight } from "lucide-react";
import { FinalCTA } from "@/components/v2/blocks";
import Seo from "@/components/v2/Seo";
import { getArticle, getAdjacent, getRelated } from "@/data/articles";
import api from "@/lib/api";

export default function Article() {
  const { slug } = useParams();
  // Contenu local par défaut, éventuellement enrichi par le backend.
  const [article, setArticle] = useState(() => getArticle(slug));

  useEffect(() => {
    window.scrollTo({ top: 0 });
    setArticle(getArticle(slug));
    api.get(`/articles/${slug}`)
      .then((r) => { if (r.data && r.data.body) setArticle(r.data); })
      .catch(() => {});
  }, [slug]);

  const title = article?.title || (slug ? slug.replace(/-/g, " ") : "Article");
  const body = article?.body || "Cet article sera publié prochainement.";
  const category = article?.category || "Veille Marché";
  const isKnownTitle = Boolean(article?.title);
  const { prev, next } = getAdjacent(slug);
  const related = getRelated(slug, 3);

  return (
    <div>
      <Seo title={title} description={article?.excerpt || "Veille Marché Energy Market."} path={`/veille-marche/${slug}`} />

      {/* HEADER */}
      <header className="em-grain border-b border-border">
        <div className="em-container max-w-3xl pt-14 pb-10 md:pt-16">
          <Link to="/veille-marche" className="inline-flex items-center gap-1 text-sm text-muted-foreground transition-colors hover:text-primary">
            <ArrowLeft className="h-4 w-4" /> Retour à la Veille Marché
          </Link>
          <div className="mt-6 flex flex-wrap items-center gap-3">
            <span className="rounded-full bg-secondary px-3 py-1 text-xs font-semibold uppercase tracking-[0.12em] text-primary">{category}</span>
            {article?.date && <span className="text-sm text-muted-foreground">{article.date}</span>}
          </div>
          <h1 className={`mt-4 font-display text-3xl md:text-4xl lg:text-[2.75rem] font-black leading-tight tracking-tight text-primary ${isKnownTitle ? "" : "capitalize"}`}>
            {title}
          </h1>
          {article?.excerpt && <p className="mt-5 text-lg leading-relaxed text-muted-foreground">{article.excerpt}</p>}
        </div>
      </header>

      {/* CORPS */}
      <article className="em-container max-w-3xl py-12">
        <div className="space-y-5 text-[1.05rem] leading-relaxed text-foreground/85">
          {body.split("\n").filter(Boolean).map((p, i) => <p key={i}>{p}</p>)}
        </div>

        {/* NAVIGATION PRÉCÉDENT / SUIVANT */}
        <div className="mt-12 grid gap-4 border-t border-border pt-8 sm:grid-cols-2">
          {prev ? (
            <Link to={`/veille-marche/${prev.slug}`} className="group rounded-2xl border border-border bg-card p-5 transition-all duration-300 hover:border-primary/40 hover:shadow-md">
              <span className="inline-flex items-center gap-1 text-xs font-semibold uppercase tracking-[0.12em] text-muted-foreground">
                <ArrowLeft className="h-3.5 w-3.5" /> Article précédent
              </span>
              <p className="mt-2 font-display text-base font-bold leading-snug text-primary transition-colors group-hover:text-primary">{prev.title}</p>
            </Link>
          ) : <span className="hidden sm:block" />}
          {next ? (
            <Link to={`/veille-marche/${next.slug}`} className="group rounded-2xl border border-border bg-card p-5 text-right transition-all duration-300 hover:border-primary/40 hover:shadow-md sm:col-start-2">
              <span className="inline-flex items-center gap-1 text-xs font-semibold uppercase tracking-[0.12em] text-muted-foreground">
                Article suivant <ArrowRight className="h-3.5 w-3.5" />
              </span>
              <p className="mt-2 font-display text-base font-bold leading-snug text-primary">{next.title}</p>
            </Link>
          ) : <span className="hidden sm:block" />}
        </div>
      </article>

      {/* À LIRE AUSSI */}
      {related.length > 0 && (
        <section className="bg-secondary">
          <div className="em-container py-14">
            <p className="em-eyebrow mb-3">À lire aussi</p>
            <h2 className="font-display text-2xl md:text-3xl font-bold tracking-tight text-primary">D'autres analyses de la Veille Marché</h2>
            <div className="mt-8 grid gap-5 md:grid-cols-3">
              {related.map((a) => (
                <Link
                  key={a.slug}
                  to={`/veille-marche/${a.slug}`}
                  className="group flex flex-col rounded-2xl border border-border bg-card p-6 transition-all duration-300 hover:-translate-y-1 hover:border-primary/40 hover:shadow-lg"
                >
                  <div className="flex items-center justify-between gap-2">
                    <p className="em-eyebrow">{a.category}</p>
                    {a.date && <span className="text-[10px] text-muted-foreground">{a.date}</span>}
                  </div>
                  <h3 className="mt-2 font-display text-lg font-bold leading-snug text-primary">{a.title}</h3>
                  <p className="mt-2 line-clamp-3 text-sm leading-relaxed text-muted-foreground">{a.excerpt}</p>
                  <span className="mt-4 inline-flex items-center gap-1 text-sm font-semibold text-primary">
                    Lire l'article <ArrowRight className="h-3.5 w-3.5 transition-transform group-hover:translate-x-0.5" />
                  </span>
                </Link>
              ))}
            </div>
          </div>
        </section>
      )}

      <FinalCTA />
    </div>
  );
}
